/* eslint-disable react-hooks/exhaustive-deps */
// Library Imports
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { normalizeSection, usePartnersListing } from "../../../lib/react-query/partners.ts";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

// Local Imports
import {
  PlusIcon,
  SendIcon,
} from "../../../assets/icons";
import { ContractGenerateIcon } from "../../../assets/icons/ContractGenerate";
import { assets } from "../../../assets/images";
import ChatAreaBody from "../../../components/Chats/ChatAreaBody";
import ChatAreaHeader from "../../../components/Chats/ChatAreaHeader";
import { ContractModal } from "../../../components/Chats/ContractModal";
import GroupChatModal from "../../../components/Chats/GroupChatModal";
import PartnerChatModal from "../../../components/Chats/PartnerChatModal";
import { UploadDropdown } from "../../../components/Chats/UploadDropdown";
import SideBarBody from "../../../components/Chats/SideBarBody";
import {
  Button,
  EmptyState,
  showToast,
  CustomPopover,
  Skeleton,
  Spinner,
} from "../../../components/shared";
import { styles } from "../../../components/shared/AuthLayout/styles";
import { DateDivider } from "../../../components/shared/Divider";
import SearchBar from "../../../components/shared/SearchBar";
import { endPoints } from "../../../redux/network/constant";
import axiosInstance from "../../../redux/network/https";
import { useSocket } from "../../../redux/socket/useSocket.js";
import { formatDateLabel, formatToReadableTime, getFullName } from "../../../utils/utils";

const Chats = () => {
  const currentUser = useSelector((state) => state?.user?.user);
  const userId = currentUser?.id;
  const {
    channels,
    messages,
    newChannel,
    sendCommand,
    sendCommandWithLoading,
    setMessages,
    setActiveChannelId,
    isSocketReady,
    loadingChannels,
    loadingMessages,
    setLoadingChannels,
    setLoadingMessages,
  } = useSocket();
  const { data: chatMembers } = usePartnersListing(null, true, {
    select: (listing) => {
      const matched = normalizeSection(listing?.matched_partners).results;
      const prioritized = normalizeSection(listing?.prioritized_partners).results;
      const base = [...matched, ...prioritized];
      return base.map((data) => {
        const partner = data?.partner || data?.matched_user || {};
        return {
          id: partner?.id || null,
          name: getFullName(partner),
          avatar: partner?.profile_image || "",
          selected: false,
          completeItem: data,
        };
      });
    },
  });
  const state = useLocation();
  const [activeChat, setActiveChat] = useState(null);
  const [message, setMessage] = useState("");
  const [searchQuery, setSearchQuery] = useState(null);
  const [channelTypeFilter, setChannelTypeFilter] = useState(null);
  const [openPopover, setOpenPopover] = useState(false);
  const [fileLoading, setFileLoading] = useState(false);
  const [openGroupChatModal, setOpenGroupChatModal] = useState(false);
  const [openPartnerChatModal, setOpenPartnerChatModal] = useState(false);
  const [openContractModal, setOpenContractModal] = useState(false);
  const scrollRef = useRef();
  const inputRef = useRef();
  const fileInputRef = useRef();
  const allUsersMembers = chatMembers || [];

  // Search Chats Function
  const handleChatSearch = useCallback(
    (value) => {
      setSearchQuery(value);
    },
    [],
  );

  const messageItems = useMemo(() => {
    if (!activeChat?.channel_id || !messages?.length) {
      return [];
    }
    let lastDateLabel = null;
    return messages
      ?.filter((item) => item?.body?.length > 0)
      ?.map((item) => {
        const dateLabel = formatDateLabel(item?.msg_time);
        const showDivider = dateLabel !== lastDateLabel;
        lastDateLabel = dateLabel;
        const key =
          item?.message_id ||
          item?.id ||
          `${item?.channel_id}-${item?.msg_time}`;
        return {
          item,
          dateLabel,
          showDivider,
          key,
        };
      });
  }, [activeChat?.channel_id, messages]);

  const getSidebarItemKey = (item) => {
    if (item?.channel_id) {
      return `channel-${item.channel_id}`;
    }
    if (item?.id) {
      return `user-${item.id}`;
    }
    if (item?.members?.[0]?.user_id) {
      return `member-${item.members[0].user_id}`;
    }
    if (item?.name) {
      return `name-${item.name}`;
    }
    return `type-${item?.channel_type || "unknown"}`;
  };


  const visibleChannels = channels || [];

  // Derived chat members from partners listing via useMemo

  // Fetch All Chats
  const fetchAllChats = (payload = {}) => {
    if (isSocketReady) {
      sendCommandWithLoading("fetch_channels", payload, setLoadingChannels);
    } else {
      console.warn("WebSocket not ready. Retrying...");
    }
  };

  // Fetch All Chats
  const markSeenMessage = (channelId) => {
    if (isSocketReady) {
      sendCommand("mark_seen_messages", { channel_id: channelId });
    } else {
      console.warn("WebSocket not ready. Retrying...");
    }
  };

  // sendMessage
  const sendMessage = (attachements, targetChannelId = activeChat?.channel_id) => {
    if (isSocketReady) {
      const file = attachements?.s3Key
        ? [
            {
              s3_key: attachements?.s3Key,
              file_name: attachements?.fileName,
              file_type: attachements?.fileType,
            },
          ]
        : [];
      const fileType = attachements?.fileType?.startsWith("image/")
        ? "image"
        : attachements?.fileType?.startsWith("video/")
          ? "video"
          : "document";
      if (targetChannelId) {
        sendCommand("new_message", {
          message: file?.length > 0 ? fileType : message,
          channel_id: targetChannelId,
          attachments: file,
        });
      } else {
        sendCommand("new_channel", {
          channel_type: activeChat?.chatType,
          members: [activeChat?.id],
          message: file?.length > 0 ? fileType : message,
          attachments: file,
        });
      }
    }
    setMessage("");
  };

  // Fetch Chat Messages
  const fetchChatMessages = (channelId) => {
    if (isSocketReady) {
      setActiveChannelId(channelId);
      sendCommandWithLoading(
        "fetch_messages",
        { channel_id: channelId },
        setLoadingMessages,
      );
    } else {
      console.warn("WebSocket not ready. Retrying...");
    }
  };

  // Create New Group Chat
  const createNewGroupChat = (ids, payload) => {
    sendCommand("new_channel", {
      channel_type: "group",
      members: ids,
      group_name: payload?.groupName,
      sub_line: payload?.groupDescription,
    });
  };

  // Delete Chat
  const deleteChat = (channelId) => {
    sendCommand("delete_channel", { channel_id: channelId });
    setActiveChat(null);
  };

  // Handle File Upload
  const handleFileUpload = (type) => {
    if (!activeChat?.channel_id) {
      showToast("Send a message first to create this chat before attaching files", "error", {
        position: "top-center",
      });
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.accept = type;
      fileInputRef.current.click();
    }
  };

  const handleFileSelected = (e) => {
    const file = e.target.files?.[0];
    if (file && !activeChat?.channel_id) {
      showToast("Send a message first to create this chat before attaching files", "error", {
        position: "top-center",
      });
      return;
    }
    if (file && file?.size > 10 * 1024 * 1024) {
      showToast("File size should be less than 10MB", "error", {
        position: "top-center",
      });
      return;
    }
    if (file) {
      const channelId = activeChat?.channel_id;
      if (file.type.startsWith("image/")) {
        // image type
      } else if (file.type.startsWith("video/")) {
        // video type
      } else {
        // document type
      }
      setFileLoading(true);
      axiosInstance
        .post(endPoints.getMediaURL, {
          files: [
            {
              file_name: file.name,
              file_type: file.type,
              file_size: file.size,
            },
          ],
          channel_id: channelId,
        })
        .then((res) => {
          const response = res?.data?.data[0];
          const formData = new FormData();
          Object.entries(response?.fields || {}).forEach(([key, value]) => formData.append(key, value));
          formData.append("file", file);
          axios
            .post(response?.upload_url, formData)
            .then(() => axiosInstance.post(endPoints.ConfirmChatMedia, {
              channel_id: channelId,
              s3_key: response?.s3_key,
            }))
            .then((confirmation) => {
              const attachment = confirmation?.data?.data;
              sendMessage({
                fileName: attachment?.file_name,
                fileType: attachment?.file_type,
                s3Key: attachment?.s3_key,
              }, channelId);
              setFileLoading(false);
            })
            .catch((err) => {
              setFileLoading(false);
              showToast(err?.response?.data?.message, "error", {
                position: "top-center",
              });
              // }
            });
        })
        .catch((err) => {
          setFileLoading(false);
          showToast(err?.response?.data?.message, "error", {
            position: "top-center",
          });
        });
    }
  };

  useEffect(function fetchChannelsOnSocketReadyOrFilterChange() {
    if (!isSocketReady) return;
    const normalizedValue = searchQuery?.trim()?.toLowerCase();
    const timeout = setTimeout(() => {
      fetchAllChats({
        q: normalizedValue || null,
        channel_type: channelTypeFilter,
      });
    }, 300);
    return () => clearTimeout(timeout);
  }, [channelTypeFilter, searchQuery, isSocketReady]);

  useEffect(function scrollToBottomOnMessagesChange() {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(function syncActiveChatFromNewChannel() {
    if (!newChannel || !activeChat) return;
    const fullChannel = channels?.find((ch) => ch.channel_id === newChannel);
    if (fullChannel) {
      setActiveChat({ ...fullChannel });
    } else {
      setActiveChat({
        ...activeChat,
        channel_id: newChannel,
      });
    }
  }, [newChannel, channels]);

  useEffect(function syncActiveChannelIdFromActiveChat() {
    setActiveChannelId(activeChat?.channel_id || null);
  }, [activeChat?.channel_id, setActiveChannelId]);

  useEffect(function openChatFromNavigationState() {
    if (state?.state?.user_id && channels == null) return;
    if (state?.state?.user_id) {
      const userChat = channels?.find(
        (channel) =>
          channel?.channel_type === "individual" &&
          channel?.members[0]?.user_id === state?.state?.user_id,
      );
      if (userChat) {
        setActiveChat(userChat);

        fetchChatMessages(userChat.channel_id);
        markSeenMessage(userChat.channel_id);
      } else {
        setActiveChat(state?.state?.completeItem);

        setSearchQuery(null);
      }
    }
    if (state?.state?.user_id) {
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search + window.location.hash,
      );
    }
  }, [state, channels]);

  const handleFilterChange = (nextFilter) => {
    setChannelTypeFilter(nextFilter);
    const normalizedValue = searchQuery?.trim()?.toLowerCase();
    fetchAllChats({
      q: normalizedValue || null,
      channel_type: nextFilter,
    });
  };

  return (
    <React.Fragment>
      {
        <div className="relative flex h-[calc(100vh-5.1rem)] my-2 bg-white overflow-x-hidden">
          {/* Inset */}
          {fileLoading && (
            <div className="inset-0 h-full w-full opacity-60 absolute z-overlay flex items-center justify-center bg-surface-disabled">
              <div className="flex gap-2">
                <div className="w-4 h-4 rounded-full animate-pulse bg-secondary/100"></div>
                <div className="w-4 h-4 rounded-full animate-pulse bg-secondary/100"></div>
                <div className="w-4 h-4 rounded-full animate-pulse bg-secondary/100"></div>
              </div>
            </div>
          )}

          {/* Chat Sidebar */}
          <div
            className={`border-r flex flex-col ${
              activeChat
                ? "hidden lg:flex lg:w-[320px] lg:shrink-0"
                : "w-full lg:w-[320px] lg:shrink-0"
            }`}
          >
            {/* Siderbar Header */}
            <div className="h-[70px] p-4 border-b flex items-center justify-between">
              <h1 className="text-xl font-semibold flex items-center gap-2">
                Active Conversations
                <span className="bg-surface-hover text-darkgrey px-2 py-0.5 rounded-full text-sm">
                  {channels?.length}
                </span>
              </h1>
              <CustomPopover
                open={openPopover}
                setOpen={() => setOpenPopover(!openPopover)}
                placement="bottom-end"
                trigger={
                  <div className="ml-auto w-9 h-9 rounded-full bg-primary text-white hover:bg-primary/90 transition-all duration-200 cursor-pointer flex items-center justify-center shadow-sm hover:shadow">
                    <PlusIcon width={22} height={22} />
                  </div>
                }
              >
                <div className="bg-white border border-surface-border shadow-2xl rounded-md font-sans text-sm flex flex-col">
                  <div
                    className="vertical-center gap-2 cursor-pointer py-2 px-2 hover:bg-surface-hover"
                    onClick={() => {
                      setOpenPartnerChatModal(true);
                      setOpenPopover(false);
                    }}
                  >
                    <p>Chat With a Connection</p>
                  </div>

                  <div
                    className="vertical-center gap-2 cursor-pointer py-2 px-2 hover:bg-surface-hover"
                    onClick={() => {
                      setOpenGroupChatModal(true);
                      setOpenPopover(false);
                    }}
                  >
                    <p>New Group</p>
                  </div>
                </div>
              </CustomPopover>
            </div>
            {/* Sidebar Body */}
            <React.Fragment>
              <div className="p-4">
                <SearchBar
                  className={"bg-surface-hover rounded"}
                  variant={"bordered"}
                  value={searchQuery || ""}
                  onValueChange={handleChatSearch}
                  placeholder={"Search"}
                  onClear={() => {
                    setSearchQuery("");
                    fetchAllChats({
                      q: null,
                      channel_type: channelTypeFilter,
                    });
                  }}
                />
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    className={`px-3 py-1 text-sm rounded-full border ${channelTypeFilter === null ? "bg-surface-active border-surface-border" : "bg-white border-surface-border"}`}
                    onClick={() => handleFilterChange(null)}
                  >
                    All
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-sm rounded-full border ${channelTypeFilter === "group" ? "bg-surface-active border-surface-border" : "bg-white border-surface-border"}`}
                    onClick={() => handleFilterChange("group")}
                  >
                    Groups
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1 text-sm rounded-full border ${channelTypeFilter === "individual" ? "bg-surface-active border-surface-border" : "bg-white border-surface-border"}`}
                    onClick={() => handleFilterChange("individual")}
                  >
                    Chats
                  </button>
                </div>
              </div>

              <div className="relative flex-1 overflow-y-auto overflow-x-hidden min-h-0">
                {loadingChannels ? (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <div className="px-4 py-4 rounded-full bg-white shadow-sm">
                      <Spinner width={28} height={28} />
                    </div>
                  </div>
                ) : null}
                {visibleChannels.length > 0 ? (
                  visibleChannels.map(
                    (item, index) => (
                      <SideBarBody
                        key={getSidebarItemKey(item) || `row-${index}`}
                        avatar={
                          item?.channel_type === "individual"
                            ? item?.members?.[0]?.profile_image || item?.avatar
                            : item?.avatar
                        }
                        lastMessage={item?.chat_message}
                        name={item?.channel_name || item?.name}
                        unReadMessages={item?.unread_messages}
                        status={item?.online}
                        setActiveChat={setActiveChat}
                        activeChat={activeChat}
                        completeItem={item}
                        fetchChatMessages={fetchChatMessages}
                        fetchAllChats={fetchAllChats}
                        setSearchQuery={setSearchQuery}
                        newChat={searchQuery?.length > 0}
                        markSeenMessage={markSeenMessage}
                        chatType={item?.channel_type}
                        setMessage={setMessage}
                        setMessages={setMessages}
                        inputRef={inputRef}
                      />
                    ),
                  )
                ) : (
                  !loadingChannels && (
                    <div className="flex flex-col items-center justify-center h-full px-2 text-center bg-white">
                      {channels?.length === 0 ? (
                        <EmptyState
                          compact
                          title="No Conversations Yet"
                          description="Start a new conversation by searching for a user"
                          className="h-full rounded-none border-0"
                        />
                      ) : (
                        <p>No Member found for Chat!</p>
                      )}
                    </div>
                  )
                )}
              </div>
            </React.Fragment>
          </div>

          {/* Chat Area */}
          <React.Fragment>
            {activeChat === null ? (
              <div
                style={{
                  backgroundImage: `url(${assets.background})`,
                  backgroundSize: "cover",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "top center",
                }}
                className="hidden lg:block flex-1 h-full overflow-hidden"
              >
                <div
                  style={styles}
                  className="h-full flex-1 flex flex-col items-center justify-center"
                >
                  <div className="flex flex-col items-center text-center font-sans animate-fade-in-up bg-primary/70 backdrop-blur-sm rounded-2xl px-10 py-8 shadow-lg">
                    <p className="text-white text-2xl sm:text-3xl font-light tracking-wide mb-2 drop-shadow-md">
                      Welcome to
                    </p>
                    <p className="text-white text-5xl sm:text-6xl base:text-7xl font-bold tracking-tight mb-6 drop-shadow-xl">
                      Chats
                    </p>
                    <p className="text-white text-lg sm:text-xl font-light tracking-wide max-w-md drop-shadow-md">
                      Please select a conversation to start a chat.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${
                  activeChat
                    ? "w-full h-full flex-1 flex flex-col"
                    : "hidden lg:flex lg:flex-1 lg:flex-col"
                }`}
              >
                <React.Fragment>
                  {/* Chat Header */}
                  <ChatAreaHeader
                    chatAvatar={activeChat?.avatar}
                    chatType={
                      activeChat?.channel_type === "group" ? "group" : "single"
                    }
                    chatHeaderName={
                      activeChat?.channel_name || activeChat?.name
                    }
                    chatMembers={activeChat?.members?.map(
                      (member) => member?.user_name,
                    )}
                    setActiveChat={setActiveChat}
                    activeChat={activeChat}
                    deleteChat={deleteChat}
                    userId={userId}
                    fetchAllChats={fetchAllChats}
                    setOpenContractModal={setOpenContractModal}
                  />

                  {/* Chat Body/ Messages */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                    {loadingMessages
                      ? Array.from({ length: 5 }, (_, index) => (
                          <div
                            key={index}
                            className={`flex gap-2 ${index % 2 === 0 ? "justify-end" : "justify-start"}`}
                          >
                            <Skeleton
                              className={`w-48 h-12 rounded-lg ${index % 2 === 0 ? "bg-chat-user/30" : "bg-surface-active"}`}
                            />
                          </div>
                        ))
                      : activeChat?.channel_id && messageItems.length > 0
                        ? messageItems.map(({ item, dateLabel, showDivider, key }) => (
                            <React.Fragment key={key}>
                              {showDivider && <DateDivider label={dateLabel} />}
                              <ChatAreaBody
                                message={item?.body}
                                name={item?.sender_name}
                                avatar={item?.sender?.avatar}
                                isSeenMessage={item?.is_seen}
                                messageTime={formatToReadableTime(
                                  item?.msg_time,
                                )}
                                senderId={item?.sender_id}
                                chatType={
                                  activeChat?.channel_type === "group"
                                    ? "group"
                                    : "single"
                                }
                                activeChat={activeChat}
                                markSeenMessage={markSeenMessage}
                                onHover={() => {
                                  if (
                                    Number(
                                      channels?.find(
                                        (channel) =>
                                          channel?.channel_id ===
                                          activeChat?.channel_id,
                                      )?.unread_messages,
                                    ) > 0
                                  ) {
                                    markSeenMessage(activeChat?.channel_id);
                                  }
                                }}
                                file={item?.attachments?.length > 0}
                                fileType={
                                  item?.attachments?.length > 0
                                    ? item?.attachments[0]?.file_type
                                    : null
                                }
                                attachements={
                                  item?.attachments?.length > 0
                                    ? item?.attachments[0]
                                    : null
                                }
                                completeItem={item}
                                isHtmlMessage={
                                  item?.message_type === "review_request"
                                }
                              />
                            </React.Fragment>
                          ))
                        : null}
                    {!loadingMessages &&
                      activeChat?.channel_id &&
                      messages?.length === 0 && (
                        <EmptyState
                          compact
                          title="No messages yet"
                          description="Start the conversation!"
                          className="h-full rounded-none border-0 bg-transparent"
                        />
                      )}
                    <div ref={scrollRef} />
                  </div>
                </React.Fragment>

                {/* Chat Footer/ Input */}
                <div className="p-4 border-t w-full">
                  <div className="flex items-center gap-2 bg-surface rounded-lg p-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv"
                      onChange={handleFileSelected}
                      onClick={(e) => {
                        e.target.value = null;
                        setMessage("");
                      }}
                    />
                    <UploadDropdown
                      disabled={!activeChat?.channel_id}
                      onUploadImage={() =>
                        handleFileUpload(
                          "image/jpeg,image/png,image/gif,image/webp",
                        )
                      }
                      onUploadDocument={() =>
                        handleFileUpload(
                          "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/csv",
                        )
                      }
                    />

                    <button
                      disabled={!activeChat?.channel_id}
                      className="p-2 cursor-pointer hover:bg-surface-hover rounded-full disabled:opacity-50"
                      onClick={() => setOpenContractModal(true)}
                      title="Create Contract"
                    >
                      <ContractGenerateIcon className="w-5 h-5 text-mediumgrey" />
                    </button>
                    <div className="h-6 w-[1px] bg-surface-disabled"></div>
                    <textarea
                      placeholder="Type something here"
                      className="flex-1 p-2 resize-none bg-transparent focus:ring-0 focus:outline-none border-none font-sans text-base"
                      rows="1"
                      style={{
                        minHeight: "2.5rem",
                        maxHeight: "10rem",
                        overflow: "hidden",
                      }}
                      value={message}
                      ref={inputRef}
                      onChange={(e) => {
                        setMessage(e.target.value);
                        // Auto-grow textarea
                        e.target.style.height = "auto";
                        e.target.style.height =
                          Math.min(e.target.scrollHeight, 160) + "px";
                      }}
                      onClick={() => {
                        if (
                          Number(
                            channels?.find(
                              (channel) =>
                                channel?.channel_id === activeChat?.channel_id,
                            )?.unread_messages,
                          ) > 0
                        ) {
                          markSeenMessage(activeChat?.channel_id);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          if (e.shiftKey) {
                            // Allow Shift+Enter to add a new line
                            return;
                          } else {
                            // Send message on plain Enter
                            e.preventDefault();
                            if (message?.length > 0) {
                              sendMessage();
                            }
                          }
                        }
                      }}
                    />
                    <Button
                      className={`${
                        message?.length > 0
                          ? "opacity-100"
                          : "opacity-40 bg-primary hover:bg-primary"
                      } bg-primary hover:bg-primary p-2 rounded min-h-9 w-[40px]`}
                      disabled={message?.length === 0}
                      onClick={message?.length > 0 && sendMessage}
                    >
                      <SendIcon className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>

          <GroupChatModal
            allUsersMembers={allUsersMembers}
            openGroupChatModal={openGroupChatModal}
            setOpenGroupChatModal={setOpenGroupChatModal}
            createNewGroupChat={createNewGroupChat}
          />

          <PartnerChatModal
            openPartnerChatModal={openPartnerChatModal}
            setOpenPartnerChatModal={setOpenPartnerChatModal}
            allUsersMembers={allUsersMembers}
          />

          {/* Contract Modal */}
          <ContractModal
            isOpen={openContractModal}
            onClose={() => setOpenContractModal(false)}
            chatChannelId={activeChat?.channel_id}
          />
        </div>
      }
    </React.Fragment>
  );
};

export default Chats;
